import {
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form'

import { ChipSelect, ChipSelectProps } from '@/components/ui/chip-select'

interface ChipSelectFormFieldProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<ChipSelectProps, 'value' | 'onChange' | 'error'> {
  name: Path<TFieldValues>
}

export const ChipSelectFormField = <TFieldValues extends FieldValues = FieldValues>({
  name,
  ...props
}: ChipSelectFormFieldProps<TFieldValues>) => {
  const { setValue, getFieldState } = useFormContext<TFieldValues>()
  const watchedValue = useWatch<TFieldValues, Path<TFieldValues>>({ name })
  const formState = useFormState<TFieldValues>({ name })
  const value: string[] = Array.isArray(watchedValue) ? watchedValue : []
  const { error } = getFieldState(name, formState)

  return (
    <ChipSelect
      {...props}
      error={error?.message}
      value={value}
      onChange={(val) =>
        setValue(name, val as PathValue<TFieldValues, typeof name>, {
          shouldDirty: true,
          shouldTouch: true,
          // "Reward Early, Punish Late": don't validate live before the first Save,
          // but once an error IS showing, re-validate on every change (RHF's default
          // reValidateMode: 'onChange', which setValue bypasses) so the message stays
          // accurate — persists while still over the limit, clears the instant the
          // value drops back within it. ChipSelect preserves dropdown scroll across
          // the render this schedules.
          shouldValidate: !!error,
        })
      }
    />
  )
}
